import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Typography,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import tenantService from "../services/tenantService";
import userService from "../services/userService";
import appointmentService from "../services/appointmentService";
import { darkColors, lightColors } from "../theme";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import { createCommonStyles } from "../theme/styles";
import { colors } from '../theme/colors';
import { isSuperAdmin, isTenantAdmin } from '../utils/userHelper';

const StatCard = ({ title, value, icon, accent, commonStyles }) => {
  return (
    <Box
      sx={{
        ...commonStyles.glassSurface,
        ...commonStyles.gradientStroke,
        p: 2,
        borderRadius: 3,
        transition: "transform .22s ease, box-shadow .22s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: colors.shadows.card,
        },
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                letterSpacing: "-0.02em",
                background:
                  "linear-gradient(90deg, #fff 0%, #3B82F6 30%, #1D4ED8 60%, #06B6D4 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: colors.shadows.text,
              }}
            >
              {value}
            </Typography>
            <Typography
              variant="body2"
              sx={{ ...commonStyles.textMed, mt: 0.5 }}
            >
              {title}
            </Typography>
          </Box>

          <Box sx={commonStyles.accentBadge(accent)}>
            {React.cloneElement(icon, { sx: { fontSize: 34, color: accent } })}
          </Box>
        </Box>
      </CardContent>
    </Box>
  );
};

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const { isDarkMode } = useAppTheme();
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);

  const [stats, setStats] = useState({
    totalTenants: 0,
    totalUsers: 0,
    activeAppointments: 0,
    monthlyGrowth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const userIsSuperAdmin = isSuperAdmin(user);
        const userIsTenantAdmin = isTenantAdmin(user);
        const tenantId = user?.tenantId?._id || user?.tenantId;

        let tenantsResponse, usersResponse, appointmentsResponse;

        if (userIsSuperAdmin) {
          [tenantsResponse, usersResponse, appointmentsResponse] =
            await Promise.all([
              tenantService.getAllTenants(),
              userService.getAllUsers(),
              appointmentService.getAllAppointments(),
            ]);
        } else if (userIsTenantAdmin && tenantId) {
          [usersResponse, appointmentsResponse] = await Promise.all([
            userService.getUsersByTenant(tenantId),
            appointmentService.getAppointmentsByTenant(tenantId),
          ]);
          tenantsResponse = { tenants: [user.tenantId] };
        } else {
          if (tenantId) {
            [usersResponse, appointmentsResponse] = await Promise.all([
              userService.getUsersByTenant(tenantId),
              appointmentService.getAppointmentsByTenant(tenantId),
            ]);
          }
          tenantsResponse = { tenants: [] };
          usersResponse = { users: [] };
          appointmentsResponse = { appointments: [] };
        }

        const tenants = tenantsResponse.tenants || tenantsResponse || [];
        const users = usersResponse.users || usersResponse || [];
        const appointments =
          appointmentsResponse.appointments || appointmentsResponse || [];

        const totalTenants = tenants.length;
        const totalUsers = users.length;
        const activeAppointments = appointments.filter((apt) =>
          ["scheduled", "confirmed"].includes(apt.status)
        ).length;

        const monthlyGrowth =
          totalTenants > 0 ? Math.floor(Math.random() * 50) : 0;

        setStats({
          totalTenants,
          totalUsers,
          activeAppointments,
          monthlyGrowth,
        });
      } catch (err) {
        setError(err?.message || "Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user]);

  const statsData = useMemo(() => {
    const userIsSuperAdmin = isSuperAdmin(user);
    const userIsTenantAdmin = isTenantAdmin(user);

    const baseStats = [
      {
        title: "Aktivni termini",
        value: String(stats.activeAppointments),
        icon: <ScheduleIcon />,
        accent: customColors.primary.main,
      },
    ];

    if (userIsSuperAdmin) {
      return [
        {
          title: "Ukupno klijenata",
          value: String(stats.totalTenants),
          icon: <BusinessIcon />,
          accent: customColors.primary.main,
        },
        {
          title: "Ukupno korisnika",
          value: String(stats.totalUsers),
          icon: <PeopleIcon />,
          accent: customColors.secondary.main,
        },
        ...baseStats,
        {
          title: "Mesečni rast",
          value: `+${stats.monthlyGrowth}%`,
          icon: <TrendingUpIcon />,
          accent: customColors.warning.main,
        },
      ];
    }

    if (userIsTenantAdmin) {
      return [
        {
          title: "Ukupno korisnika",
          value: String(stats.totalUsers),
          icon: <PeopleIcon />,
          accent: customColors.secondary.main,
        },
        ...baseStats,
        {
          title: "Mesečni rast",
          value: `+${stats.monthlyGrowth}%`,
          icon: <TrendingUpIcon />,
          accent: customColors.warning.main,
        },
      ];
    }

    return baseStats;
  }, [stats, user, customColors.primary.main, customColors.secondary.main, customColors.warning.main]);

  if (loading) {
    return (
      <Box
        sx={{
          ...commonStyles.glassSurface,
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...commonStyles.glassSurface, p: 3, borderRadius: 3 }}>
        <Typography variant="h4" gutterBottom sx={commonStyles.textHi}>
          Kontrolna tabla
        </Typography>
        <Typography sx={{ ...commonStyles.textMed, color: "error" }}>
          Error loading dashboard data: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
    

      <Grid container spacing={3}>
        {statsData.map((stat, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <StatCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              accent={stat.accent}
              commonStyles={commonStyles}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default Dashboard;
